import TF from "@/components/forms/TextField.module.css";

import { DeleteButton } from "@/components/button/DeleteButton.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { formPropsToAriaProps } from "@/components/forms/ariaController.tsx";
import { RhfError } from "@/components/forms/FormFieldErrors.tsx";
import { requiredMessage } from "@/components/forms/requiredMessage.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { WTable, WTableBody } from "@/components/table/WTable.tsx";
import { validatePort, validateSourcePort } from "@/modules/network/loadbalancer/validatePorts.ts";
import type { BalancerCreateInputs } from "@/modules/network/NetworkCreate.tsx";
import { Trans, useLingui } from "@lingui/react/macro";
import { Input, Label, NumberField } from "react-aria-components";
import { Controller, useFieldArray, useFormContext } from "react-hook-form";

export function ForwardingRulesList() {
    const form = useFormContext<BalancerCreateInputs>();
    const { t } = useLingui();
    const { control } = form;
    const { fields, append, remove } = useFieldArray({ control, name: "rules" });

    function onAdd() {
        // XXX: `append` is broken for some reason
        void append;
        // append([{ source_port: 1, target_port: 2 }]);
        form.setValue("rules", [...form.getValues("rules"), { source_port: NaN, target_port: NaN }]);
    }

    function onDelete(index: number) {
        remove(index);

        const currentRules = form.getValues("rules");
        const updatedRules = currentRules.filter((_, i) => i !== index);
        form.setValue("rules", updatedRules);
    }

    return (
        <WTable
            afterTable={
                <WButton
                    color="primary"
                    variant="dashed"
                    icon="jp-icon-add"
                    action={() => onAdd()}
                    className="w-full"
                    isDisabled={fields.length >= 8}
                >
                    <Trans>Add new rule</Trans>
                </WButton>
            }
        >
            <thead>
                <tr>
                    <th>
                        <Trans>Protocol</Trans>
                    </th>
                    <th className="text-right">
                        <Trans>Port</Trans>
                    </th>
                    <th />
                    <th>
                        <Trans>Target port</Trans>
                    </th>
                    <th />
                </tr>
            </thead>
            <WTableBody>
                {fields.map((item, index) => (
                    <tr key={item.id}>
                        <td>TCP</td>
                        <td className="text-right">
                            <Controller
                                control={control}
                                name={`rules.${index}.source_port`}
                                rules={{
                                    required: t(requiredMessage),
                                    validate: (v, f) => validateSourcePort(v, f.rules, true),
                                }}
                                render={(f) => (
                                    <NumberField
                                        className={TF.NumberField}
                                        isRequired
                                        formatOptions={{
                                            useGrouping: false,
                                        }}
                                        {...formPropsToAriaProps(f)}
                                    >
                                        <Label className="hidden">
                                            <Trans>Source port</Trans>
                                        </Label>
                                        <div className="w-7.5rem ml-auto">
                                            <Input className={TF.Input} />
                                            <RhfError error={f.fieldState.error} />
                                        </div>
                                    </NumberField>
                                )}
                            />
                        </td>
                        <td className="text-center">
                            <MaskIcon className="jp-arrow-thin-right-icon size-1.25rem color-muted" />
                        </td>
                        <td>
                            <Controller
                                control={control}
                                name={`rules.${index}.target_port`}
                                rules={{
                                    required: t(requiredMessage),
                                    validate: (v) => validatePort(v),
                                }}
                                render={(f) => (
                                    <NumberField
                                        className={TF.NumberField}
                                        isRequired
                                        formatOptions={{
                                            useGrouping: false,
                                        }}
                                        {...formPropsToAriaProps(f)}
                                    >
                                        <Label className="hidden">
                                            <Trans>Target port</Trans>
                                        </Label>
                                        <div className="w-7.5rem">
                                            <Input className={TF.Input} />
                                            <RhfError error={f.fieldState.error} />
                                        </div>
                                    </NumberField>
                                )}
                            />
                        </td>
                        <td className="text-right">
                            <DeleteButton inTable action={() => onDelete(index)} />
                        </td>
                    </tr>
                ))}
            </WTableBody>
        </WTable>
    );
}
